import { useEffect, useReducer, useState } from 'react'

import { Box, Divider, Grid, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Stack } from '@mui/system'
import Summary from 'components/summary/summary'
import { List, ListItem } from 'components/ui'
import { useMappedTranslation } from 'hooks'
import { Device, SigninDeviceProps } from 'pages/dashboard/home/home'
import { EllipsisStyle } from 'styles/theme/theme'

import { AutomationTagIds } from '../../../../assets/test/test-automation-tag-id'
import { DeviceStatus, getPki } from '../api'
import IconDevice from '../assets/images/device.svg'
import { getDeviceIcon } from '../deviceMap'
import { useRegisteredDevices } from '../hooks/use-registered-devices'

type Payload = { action: 'SET'; devices: Device[] } | { action: 'UPDATE' }

const initialReducer = {
  devices: [] as Device[]
}

function devicesReducer(state: typeof initialReducer, payload: Payload) {
  switch (payload.action) {
    case 'SET':
      return {
        devices: payload.devices
      }
    case 'UPDATE':
      return {
        ...state
      }
  }
}

const DeviceSummary = ({ signinDevices, isSigninDevicesLoading }: SigninDeviceProps) => {
  const t = useMappedTranslation()
  const theme = useTheme()

  const MAX_ITEM = 2

  const [isLoading, setIsLoading] = useState(true)
  const [signinProductsCount, setSigninProductsCount] = useState<number>(0)
  const [productCount, setProductCount] = useState<number>(0)
  const [allProductCount, setAllProductCount] = useState<number>(0)
  const [reducer, dispatchDevices] = useReducer(devicesReducer, initialReducer)

  const { fetchRegisteredDevices } = useRegisteredDevices(true)

  useEffect(() => {
    if (isSigninDevicesLoading) {
      return
    }
    fetchRegisteredDevices()
      .then((registeredDevices) => {
        const allProducts: Device[] = []

        const products = registeredDevices.map((registeredDevice) => {
          return {
            name: registeredDevice.modelName,
            deviceName: registeredDevice.modelName,
            time: registeredDevice.displayDate,
            type: registeredDevice.productType,
            modelCode: registeredDevice.modelCode,
            status: DeviceStatus.REGISTERED
          } as Device
        })

        allProducts.push(...signinDevices, ...products)

        dispatchDevices({ action: 'SET', devices: allProducts })

        allProducts.forEach((device) => {
          getPki(device.modelCode)
            .then((productResource) => {
              const image = productResource.images.find((image) => image.resolution === '120x120')
              if (image) {
                device.imageUrl = image.url
              } else {
                device.imageUrl = productResource.images[0].url
              }
              if (device.status === DeviceStatus.REGISTERED) {
                device.deviceName = productResource.marketingName || device.modelName || device.modelCode
              }
            })
            .catch((e) => {
              device.imageUrl = getDeviceIcon(device.type, theme.palette.mode)
              if (device.status === DeviceStatus.REGISTERED) {
                device.deviceName = device.modelName || device.modelCode
              }
            })
            .finally(() => {
              dispatchDevices({ action: 'UPDATE' })
            })
        })

        const signinProductsCount = signinDevices.length
        const productCount = products.length
        const allProductCount = allProducts.length

        setSigninProductsCount(signinProductsCount)
        setProductCount(productCount)
        setAllProductCount(allProductCount)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isSigninDevicesLoading])

  return (
    <Summary
      pageUrl={'/dashboard/devices'}
      isLoading={isLoading}
      paperSx={{
        paddingBottom: { xs: '24px !important', md: '40px' },
        '& > div > .MuiGrid-root': { marginBottom: { xs: '16px', md: '30px' } }
      }}
      titleIcon={IconDevice}
      title={t('devices.title')}
      content={
        <>
          <Stack
            direction='row'
            divider={<Divider orientation='vertical' sx={{ marginX: '6.5px' }} />}
            sx={{ width: '100%', textAlign: 'center', alignItems: 'center', marginTop: 0, height: '74px' }}
          >
            <Grid sx={{ flex: '1', minWidth: 0 }}>
              <Box sx={{ ...EllipsisStyle }}>
                <Typography
                  fontSize='36px'
                  fontWeight='300'
                  lineHeight='42px'
                  sx={{ color: theme.palette.text.primary, pb: '8px' }}
                  data-testid={AutomationTagIds['cards.dashboard-recent-signins']}
                >
                  {signinProductsCount}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, width: '100%', lineHeight: '14px', height: '14px' }}>
                <Typography variant='label2' sx={{ color: theme.palette.secondary.light, lineHeight: '14px' }}>
                  {t('devices.recentSignIn')}
                </Typography>
              </Box>
            </Grid>
            <Grid sx={{ flex: '1', minWidth: 0 }}>
              <Box sx={{ width: '100%', ...EllipsisStyle }} data-testid={AutomationTagIds['cards.dashboard-registered-devices']}>
                <Typography
                  fontSize='36px'
                  fontWeight='300'
                  lineHeight='42px'
                  sx={{ color: theme.palette.text.primary, pb: '8px' }}
                >
                  {productCount}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, width: '100%', lineHeight: '14px', height: '14px' }}>
                <Typography variant='label2' sx={{ color: theme.palette.secondary.light, lineHeight: '14px' }}>
                  {t('devices.registeredDevices')}
                </Typography>
              </Box>
            </Grid>
          </Stack>
          {allProductCount === 0 ? (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                pt: { xs: '48px', md: '96px' },
                pb: { xs: '18px', md: '56px' }
              }}
              data-testid={AutomationTagIds['cards.dashboard-recent-activity-devices']}
            >
              <Typography>{t('devices.recentSignIn.noActivities')}</Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', marginTop: '16px' }}>
              <List sx={{ py: '14px' }}>
                {reducer.devices.slice(0, MAX_ITEM).map((device, index) => (
                  <ListItem
                    key={index}
                    index={index}
                    icon={device.imageUrl}
                    iconSx={{ width: '40px', height: '40px' }}
                    iconItemSx={{ minWidth: { xs: '52px', md: '56px' } }}
                    title={
                      <>
                        <Box>
                          <Typography noWrap>{device.deviceName}</Typography>
                        </Box>
                      </>
                    }
                    titleSx={{ ...EllipsisStyle, width: '100%' }}
                    description={
                      <Typography
                        variant='listItemDescription'
                        sx={{
                          wordBreak: 'break-all',
                          height: { xs: '18px', md: '22px' },
                          lineHeight: { xs: '18px', md: '22px' }
                        }}
                      >
                        {device.time}
                      </Typography>
                    }
                    fullWidth={true}
                    dividerSx={{ marginLeft: { xs: '52px', md: '56px' } }}
                    stackSx={{ overflow: 'hidden', gap: { xs: '0', md: '2px' } }}
                  />
                ))}
              </List>
              {allProductCount > MAX_ITEM && (
                <Box sx={{ mx: '64px' }}>
                  <Typography variant='label2' sx={{ color: theme.palette.secondary.light }}>
                    + {allProductCount - MAX_ITEM} {t('common.button.more')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      }
    />
  )
}

export default DeviceSummary
